import * as THREE from 'three';
import { mergeGeometries } from 'three/addons/utils/BufferGeometryUtils.js';

/** Completed rigid fittings sharing a moving parent retain the same animation and material. */
export function mergeRigidParts(root: THREE.Object3D) {
  const parents: THREE.Object3D[] = [];
  root.traverse(object => { if (object instanceof THREE.Group || object instanceof THREE.Bone) parents.push(object); });
  for (const parent of parents) {
    const batches = new Map<string, THREE.Mesh[]>();
    for (const child of parent.children) {
      if (!(child instanceof THREE.Mesh) || child instanceof THREE.SkinnedMesh || child instanceof THREE.InstancedMesh || child.children.length || Array.isArray(child.material) || Object.keys(child.geometry.morphAttributes).length) continue;
      if (child.onBeforeRender !== THREE.Object3D.prototype.onBeforeRender || child.onAfterRender !== THREE.Object3D.prototype.onAfterRender || child.geometry.drawRange.start !== 0 || child.geometry.drawRange.count !== Infinity) continue;
      const key = JSON.stringify([child.material.uuid, child.visible, child.castShadow, child.receiveShadow, child.layers.mask, child.renderOrder,
        child.customDepthMaterial?.uuid, child.customDistanceMaterial?.uuid, Object.keys(child.geometry.attributes).sort().map(name => {
          const attribute = child.geometry.getAttribute(name); return [name, attribute.itemSize, attribute.normalized, attribute.array.constructor.name];
        })]);
      const batch = batches.get(key) ?? []; batch.push(child); batches.set(key, batch);
    }
    for (const parts of batches.values()) {
      if (parts.length < 2) continue;
      const geometries = parts.map(part => { part.updateMatrix(); return part.geometry.clone().applyMatrix4(part.matrix); });
      if (geometries.some(geometry => geometry.index)) for (const geometry of geometries)
        if (!geometry.index) geometry.setIndex(Array.from({ length: geometry.getAttribute('position').count }, (_, i) => i));
      const geometry = mergeGeometries(geometries);
      for (const temporary of geometries) temporary.dispose();
      if (!geometry) continue;
      const first = parts[0], object = new THREE.Mesh(geometry, first.material);
      object.name = first.name; object.visible = first.visible; object.castShadow = first.castShadow; object.receiveShadow = first.receiveShadow;
      object.layers.mask = first.layers.mask; object.renderOrder = first.renderOrder;
      object.customDepthMaterial = first.customDepthMaterial; object.customDistanceMaterial = first.customDistanceMaterial;
      object.matrixAutoUpdate = false;
      for (const part of parts) { part.removeFromParent(); part.geometry.dispose(); }
      parent.add(object);
    }
  }
  separateRigidMaterials(root);
}

/**
 * A material drawn by both skinned and rigid meshes makes three pick its program again on every draw that alternates between them.
 * Rigid parts get one copy of each such material that keeps its shader hooks, and a copy goes when its source is disposed.
 * Later changes reach a copy only by walking the figure's meshes, as wear finishing, fog of war, dissolves and unit opacity do.
 */
export function separateRigidMaterials(root: THREE.Object3D) {
  const skinned = new Set<THREE.Material>(), rigid: THREE.Mesh[] = [];
  root.traverse(object => {
    if (!(object instanceof THREE.Mesh) || Array.isArray(object.material)) return;
    if (object instanceof THREE.SkinnedMesh) skinned.add(object.material); else rigid.push(object);
  });
  const copies = new Map<THREE.Material, THREE.Material>();
  for (const mesh of rigid) {
    const source = mesh.material as THREE.Material;
    if (!skinned.has(source)) continue;
    let copy = copies.get(source);
    if (!copy) {
      // `copy` drops the compile hooks and JSON-copies userData, which would cut shared uniform holders loose.
      const userData = source.userData; source.userData = {};
      copy = source.clone(); source.userData = userData; copy.userData = { ...userData };
      if (Object.hasOwn(source, 'onBeforeCompile')) copy.onBeforeCompile = source.onBeforeCompile;
      if (Object.hasOwn(source, 'customProgramCacheKey')) copy.customProgramCacheKey = source.customProgramCacheKey;
      const owned = copy;
      source.addEventListener('dispose', () => owned.dispose());
      copies.set(source, copy);
    }
    mesh.material = copy;
  }
}
